import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import IdleTimeoutDialog from './IdleTimeoutDialog';
import LongRunningOperationIndicator from './LongRunningOperationIndicator';
import PendingRequestsBanner from '../auth/PendingRequestsBanner';
import { initIdleTimeout, cleanupIdleTimeout } from '../../services/idleTimeoutService';
import { isAuthenticated, isRememberMe } from '../../services/authService';

/**
 * Layout component for authenticated pages
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Layout component
 */
const Layout = ({ children }) => {
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  
  useEffect(() => {
    // Only initialize idle timeout if user is authenticated
    // This allows Layout to be used for public pages too
    if (isAuthenticated()) {
      // Skip idle timeout when user chose "Remember Me" (token valid for 1 day)
      if (isRememberMe()) {
        return;
      }
      initIdleTimeout({
        timeoutDuration: 30 * 60 * 1000, // 30 minutes
        warningTime: 60 * 1000, // 1 minute warning
        onWarning: () => setShowTimeoutWarning(true)
      });
      
      // Clean up on unmount
      return () => {
        cleanupIdleTimeout();
      };
    }
  }, []);
  
  // Handle closing the warning dialog
  const handleCloseWarning = () => {
    setShowTimeoutWarning(false);
  };
  return (
    <Box display="flex" flexDirection="column" minHeight="100vh">
      {/* Idle timeout warning dialog */}
      <IdleTimeoutDialog 
        open={showTimeoutWarning} 
        onClose={handleCloseWarning} 
        countdownDuration={60000} // 1 minute countdown
      />
      {/* Long-running operation indicator */}
      <LongRunningOperationIndicator />
      <Header />
      <PendingRequestsBanner />
      <Box display="flex" flexGrow={1} overflow="hidden">
        <Sidebar />
        <Box 
          component="main" 
          flexGrow={1} 
          bgcolor="background.default" 
          overflow="auto"
          sx={{ 
            width: 'calc(100vw - 250px)',
            maxWidth: 'calc(100vw - 250px)',
            overflowX: 'auto', // Allow horizontal scrolling if needed
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box 
            flexGrow={1}
            sx={{
              p: 3,
              animation: 'fadeIn 0.3s ease-out',
              '@keyframes fadeIn': {
                from: {
                  opacity: 0,
                  transform: 'translateY(10px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            {children}
          </Box>
          <Footer />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
